import { useState } from 'react'
import {
  Copy,
  Link2,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { PackagingPpwrComplianceFields } from '@/shared/components/packaging/PackagingPpwrComplianceFields'
import {
  buildPackagingManufacturerPayload,
  isPackagingManufacturerSectionValid,
  PackagingManufacturerSection,
} from '@/shared/components/packaging/PackagingManufacturerSection'
import { PackagingVolumeCm3Field } from '@/shared/components/packaging/PackagingVolumeCm3Field'
import {
  isValidPackagingVolumeCm3Input,
  parsePackagingVolumeCm3Input,
} from '@/shared/components/packaging/componentNumericInput'
import {
  useCreatePackagingItem,
  useDeletePackagingItem,
  useDeletePackagingSupplier,
  useGetPackagingSupplier,
  useRotatePackagingSupplierMagicLink,
  useUpdatePackagingSupplier,
  type PackagingPpwrComplianceLevel,
} from '@/shared/hooks/api/packagingSuppliers'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { cn } from '@/shared/lib/utils'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { PackagingComplianceProgressBadge } from './PackagingComplianceProgressBadge'
import { PackedItemsFunnelProgressBar } from './PackedItemsFunnelProgressBar'
import { derivePackedItemsFunnelStatsFromItems } from './packedItemsOverviewStats'
import { PackagingSupplierOnboardingStatus } from './PackagingSupplierOnboardingStatus'
import {
  SupplierDetailPackagingCatalog,
  type AddPackagingItemTarget,
} from './SupplierDetailPackagingCatalog'
import { mapSupplierPackagingCatalogRows } from './mapSupplierPackagingCatalogRows'
import { PackagedItemMultiSelect } from './PackagedItemMultiSelect'
import { SupplierDocumentLibraryTable } from './SupplierDocumentLibraryTable'

type SupplierEditableField = 'name' | 'address' | 'contactName' | 'contactEmail'

function isValidOptionalEmail(value: string): boolean {
  if (value.length === 0) {
    return true
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export function PackagingSupplierDetailsPage() {
  const { packagingSupplierId } = useParams<{ packagingSupplierId: string }>()
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const navigate = useNavigate()
  const canWriteOrgData = useCanWriteOrgData()
  const { data: supplier, isLoading } =
    useGetPackagingSupplier(packagingSupplierId)
  const rotateMutation = useRotatePackagingSupplierMagicLink(
    packagingSupplierId ?? ''
  )
  const deleteSupplierMutation = useDeletePackagingSupplier()
  const updateSupplierMutation = useUpdatePackagingSupplier(
    packagingSupplierId ?? ''
  )
  const createItemMutation = useCreatePackagingItem(packagingSupplierId ?? '')
  const deleteItemMutation = useDeletePackagingItem(packagingSupplierId ?? '')

  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [editingField, setEditingField] =
    useState<SupplierEditableField | null>(null)
  const [editValue, setEditValue] = useState('')
  const [isAddItemOpen, setIsAddItemOpen] = useState(false)
  const [addItemTarget, setAddItemTarget] =
    useState<AddPackagingItemTarget | null>(null)
  const [itemName, setItemName] = useState('')
  const [itemDescription, setItemDescription] = useState('')
  const [itemIntendedUse, setItemIntendedUse] = useState('')
  const [itemSku, setItemSku] = useState('')
  const [itemPackedItemSku, setItemPackedItemSku] = useState('')
  const [itemVolumeCm3Input, setItemVolumeCm3Input] = useState('')
  const [itemSupplierIsManufacturer, setItemSupplierIsManufacturer] = useState<
    boolean | undefined
  >(undefined)
  const [itemManufacturerName, setItemManufacturerName] = useState('')
  const [itemManufacturerAddress, setItemManufacturerAddress] = useState('')
  const [itemHeavyMetalsStatus, setItemHeavyMetalsStatus] = useState<
    PackagingPpwrComplianceLevel | undefined
  >(undefined)
  const [itemSubstancesOfConcernStatus, setItemSubstancesOfConcernStatus] =
    useState<PackagingPpwrComplianceLevel | undefined>(undefined)
  const [itemPackagedItemIds, setItemPackagedItemIds] = useState<string[]>([])

  const ppwrComplianceLabels = {
    heavyMetals: t('ppwrCompliance.heavyMetals'),
    substancesOfConcern: t('ppwrCompliance.substancesOfConcern'),
    none: t('ppwrCompliance.none'),
    sufficientlyMinimized: t('ppwrCompliance.sufficientlyMinimized'),
    heavyMetalsSufficientlyMinimizedHelp: t(
      'ppwrCompliance.heavyMetalsSufficientlyMinimizedHelp'
    ),
    notSufficientlyMinimized: t('ppwrCompliance.notSufficientlyMinimized'),
  }

  const copyInviteUrl = async () => {
    if (!supplier?.magicLinkUrl) {
      return
    }
    await navigator.clipboard.writeText(supplier.magicLinkUrl)
    toast.success(t('invite.copied'))
  }

  const handleRotate = async () => {
    const rotated = await rotateMutation.mutateAsync()
    await navigator.clipboard.writeText(rotated.magicLinkUrl)
    toast.success(t('invite.rotated'))
  }

  const handleDeleteSupplier = async () => {
    if (!packagingSupplierId) {
      return
    }
    await deleteSupplierMutation.mutateAsync(packagingSupplierId)
    setIsDeleteConfirmOpen(false)
    navigate('/packaging/suppliers')
  }

  const openFieldEditor = (field: SupplierEditableField) => {
    if (!supplier) {
      return
    }
    if (field === 'name') {
      setEditValue(supplier.name)
    } else if (field === 'address') {
      setEditValue(supplier.address ?? '')
    } else if (field === 'contactName') {
      setEditValue(supplier.contactName ?? '')
    } else {
      setEditValue(supplier.contactEmail ?? '')
    }
    setEditingField(field)
  }

  const closeFieldEditor = () => {
    setEditingField(null)
    setEditValue('')
  }

  let fieldEditorTitle = t('details.editContactEmailTitle')
  let fieldEditorLabel = t('details.contactEmail')
  if (editingField === 'name') {
    fieldEditorTitle = t('details.renameTitle')
    fieldEditorLabel = t('form.name')
  } else if (editingField === 'address') {
    fieldEditorTitle = t('details.editAddressTitle')
    fieldEditorLabel = t('form.address')
  } else if (editingField === 'contactName') {
    fieldEditorTitle = t('details.editContactNameTitle')
    fieldEditorLabel = t('details.contactName')
  }

  const handleSaveField = async () => {
    if (!editingField || !packagingSupplierId) {
      return
    }
    const trimmed = editValue.trim()
    if (editingField === 'name') {
      if (trimmed.length === 0 || trimmed === supplier?.name) {
        return
      }
      await updateSupplierMutation.mutateAsync({ name: trimmed })
      toast.success(t('details.renameSuccess'))
    } else if (editingField === 'address') {
      const nextAddress = trimmed.length > 0 ? trimmed : null
      if (nextAddress === (supplier?.address?.trim() || null)) {
        return
      }
      await updateSupplierMutation.mutateAsync({ address: nextAddress })
      toast.success(t('details.addressSaveSuccess'))
    } else if (editingField === 'contactName') {
      const nextContactName = trimmed.length > 0 ? trimmed : null
      if (nextContactName === (supplier?.contactName?.trim() || null)) {
        return
      }
      await updateSupplierMutation.mutateAsync({ contactName: nextContactName })
      toast.success(t('details.contactNameSaveSuccess'))
    } else {
      if (!isValidOptionalEmail(trimmed)) {
        toast.error(t('details.invalidEmail'))
        return
      }
      const nextContactEmail = trimmed.length > 0 ? trimmed : null
      if (nextContactEmail === (supplier?.contactEmail?.trim() || null)) {
        return
      }
      await updateSupplierMutation.mutateAsync({
        contactEmail: nextContactEmail,
      })
      toast.success(t('details.contactEmailSaveSuccess'))
    }
    closeFieldEditor()
  }

  const handleCreateItem = async () => {
    if (
      !itemName.trim() ||
      !itemDescription.trim() ||
      !itemIntendedUse.trim() ||
      !itemSku.trim() ||
      !itemHeavyMetalsStatus ||
      !itemSubstancesOfConcernStatus ||
      itemSupplierIsManufacturer === undefined ||
      !isPackagingManufacturerSectionValid(
        itemSupplierIsManufacturer,
        itemManufacturerName,
        itemManufacturerAddress
      )
    ) {
      return
    }
    if (
      addItemTarget?.type === 'forPart' &&
      itemPackagedItemIds.length === 0
    ) {
      return
    }
    if (!packagingSupplierId) {
      return
    }
    const manufacturerPayload = buildPackagingManufacturerPayload(
      itemSupplierIsManufacturer,
      itemManufacturerName,
      itemManufacturerAddress
    )
    let assignmentPayload:
      | {
          packagesOwnProduct: true
          packagedItemIds: string[]
          packedItemSku: string | null
        }
      | {
          packagesOwnProduct: false
          packagedItemIds: string[]
          packedItemSku: string | null
        }

    if (addItemTarget?.type === 'packedItem') {
      assignmentPayload = {
        packagesOwnProduct: true,
        packagedItemIds: [addItemTarget.packagedItemId],
        packedItemSku: addItemTarget.packedItemSku,
      }
    } else if (addItemTarget?.type === 'forPart') {
      assignmentPayload = {
        packagesOwnProduct: true,
        packagedItemIds: itemPackagedItemIds,
        packedItemSku: itemPackedItemSku.trim() || null,
      }
    } else {
      assignmentPayload = {
        packagesOwnProduct: false,
        packagedItemIds: [],
        packedItemSku: itemPackedItemSku.trim() || null,
      }
    }
    const created = await createItemMutation.mutateAsync({
      name: itemName.trim(),
      description: itemDescription.trim(),
      intendedUse: itemIntendedUse.trim(),
      sku: itemSku.trim(),
      volumeCm3: parsePackagingVolumeCm3Input(itemVolumeCm3Input),
      ...assignmentPayload,
      ...manufacturerPayload,
      heavyMetalsStatus: itemHeavyMetalsStatus,
      substancesOfConcernStatus: itemSubstancesOfConcernStatus,
      components: [],
    })
    setIsAddItemOpen(false)
    setAddItemTarget(null)
    setItemName('')
    setItemDescription('')
    setItemIntendedUse('')
    setItemSku('')
    setItemPackedItemSku('')
    setItemVolumeCm3Input('')
    setItemSupplierIsManufacturer(undefined)
    setItemManufacturerName('')
    setItemManufacturerAddress('')
    setItemHeavyMetalsStatus(undefined)
    setItemSubstancesOfConcernStatus(undefined)
    setItemPackagedItemIds([])
    navigate(`/packaging/suppliers/${packagingSupplierId}/items/${created.id}`)
  }

  const openAddItemDialog = (target: AddPackagingItemTarget) => {
    setAddItemTarget(target)
    setItemPackedItemSku(
      target.type === 'packedItem' ? target.packedItemSku : ''
    )
    setItemPackagedItemIds(
      target.type === 'packedItem' ? [target.packagedItemId] : []
    )
    setIsAddItemOpen(true)
  }

  const closeAddItemDialog = () => {
    setIsAddItemOpen(false)
    setAddItemTarget(null)
    setItemName('')
    setItemDescription('')
    setItemIntendedUse('')
    setItemSku('')
    setItemPackedItemSku('')
    setItemVolumeCm3Input('')
    setItemSupplierIsManufacturer(undefined)
    setItemManufacturerName('')
    setItemManufacturerAddress('')
    setItemHeavyMetalsStatus(undefined)
    setItemSubstancesOfConcernStatus(undefined)
    setItemPackagedItemIds([])
  }

  if (isLoading || !supplier) {
    return (
      <>
        <Header fixed>
          <Search />
          <div className='ms-auto flex items-center space-x-4'>
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
          <div className='py-12 text-center text-muted-foreground'>
            {t('loading')}
          </div>
        </Main>
      </>
    )
  }

  const items = supplier.packagingItems ?? []
  const packagedItems = supplier.packagedItems ?? []
  const allPackagingItems = mapSupplierPackagingCatalogRows(items)
  const completedPackagingItemCount = items.filter(
    (item) => item.completedAt != null
  ).length
  const totalPackagingItemCount = items.length
  const packedItemsFunnelStats =
    derivePackedItemsFunnelStatsFromItems(packagedItems)
  const hasSupplierAddress = (supplier.address?.trim() ?? '').length > 0
  const manufacturerLabels = {
    question: t('items.manufacturer.question'),
    optionYes: t('items.manufacturer.optionYes'),
    optionNo: t('items.manufacturer.optionNo'),
    manufacturerName: t('items.manufacturer.manufacturerName'),
    manufacturerAddress: t('items.manufacturer.manufacturerAddress'),
    manufacturerNameRequired: t('items.manufacturer.manufacturerNameRequired'),
    manufacturerAddressRequired: t(
      'items.manufacturer.manufacturerAddressRequired'
    ),
  }
  const hasRequiredPartLinksForCreate =
    addItemTarget?.type !== 'forPart' || itemPackagedItemIds.length > 0
  const canCreateItem =
    hasSupplierAddress &&
    itemName.trim().length > 0 &&
    itemDescription.trim().length > 0 &&
    itemIntendedUse.trim().length > 0 &&
    itemSku.trim().length > 0 &&
    isValidPackagingVolumeCm3Input(itemVolumeCm3Input) &&
    isPackagingManufacturerSectionValid(
      itemSupplierIsManufacturer,
      itemManufacturerName,
      itemManufacturerAddress
    ) &&
    itemHeavyMetalsStatus !== undefined &&
    itemSubstancesOfConcernStatus !== undefined &&
    hasRequiredPartLinksForCreate

  let addItemDialogTitle = t('items.add')
  if (addItemTarget?.type === 'packedItem') {
    addItemDialogTitle = t('details.packagingCatalog.addForPackedItem')
  } else if (addItemTarget?.type === 'forPart') {
    addItemDialogTitle = t('details.yourPackagingItems.addForPartOption')
  }

  return (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main className='flex flex-1 flex-col gap-6 px-6'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='space-y-2'>
            <h2 className='text-2xl font-bold tracking-tight'>
              {supplier.name}
            </h2>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to='/packaging/suppliers'>{t('tabs.suppliers')}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className='max-w-[min(100%,28rem)] truncate'>
                    {supplier.name}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          {canWriteOrgData && (
            <div className='flex flex-wrap gap-2'>
              <Button variant='outline' onClick={() => setIsInviteOpen(true)}>
                <Link2 className='size-4' />
                {t('invite.open')}
              </Button>
              <Button
                variant='destructive'
                onClick={() => {
                  setIsDeleteConfirmOpen(true)
                }}
              >
                <Trash2 className='size-4' />
                {t('details.delete')}
              </Button>
            </div>
          )}
        </div>

        <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('form.name')}</ItemTitle>
              <ItemDescription>{supplier.name}</ItemDescription>
            </ItemContent>
            {canWriteOrgData && (
              <ItemActions>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => openFieldEditor('name')}
                  disabled={updateSupplierMutation.isPending}
                  aria-label={t('details.renameAria')}
                >
                  <Pencil className='size-4' />
                </Button>
              </ItemActions>
            )}
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('form.address')}</ItemTitle>
              <ItemDescription>
                {supplier.address?.trim() || t('details.noContact')}
              </ItemDescription>
            </ItemContent>
            {canWriteOrgData && (
              <ItemActions>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => openFieldEditor('address')}
                  disabled={updateSupplierMutation.isPending}
                  aria-label={t('details.editAddressAria')}
                >
                  <Pencil className='size-4' />
                </Button>
              </ItemActions>
            )}
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('details.contactName')}</ItemTitle>
              <ItemDescription>
                {supplier.contactName?.trim() || t('details.noContact')}
              </ItemDescription>
            </ItemContent>
            {canWriteOrgData && (
              <ItemActions>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => openFieldEditor('contactName')}
                  disabled={updateSupplierMutation.isPending}
                  aria-label={t('details.editContactNameAria')}
                >
                  <Pencil className='size-4' />
                </Button>
              </ItemActions>
            )}
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('details.contactEmail')}</ItemTitle>
              <ItemDescription>
                {supplier.contactEmail?.trim() || t('details.noContact')}
              </ItemDescription>
            </ItemContent>
            {canWriteOrgData && (
              <ItemActions>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => openFieldEditor('contactEmail')}
                  disabled={updateSupplierMutation.isPending}
                  aria-label={t('details.editContactEmailAria')}
                >
                  <Pencil className='size-4' />
                </Button>
              </ItemActions>
            )}
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('list.columnOnboardingStatus')}</ItemTitle>
              <ItemDescription>
                <PackagingSupplierOnboardingStatus
                  magicLinkFirstOpenedAt={supplier.magicLinkFirstOpenedAt}
                  ppwrComplianceAcknowledgedAt={
                    supplier.ppwrComplianceAcknowledgedAt
                  }
                />
              </ItemDescription>
            </ItemContent>
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('list.columnComplianceProgress')}</ItemTitle>
              <ItemDescription>
                <PackagingComplianceProgressBadge
                  completed={completedPackagingItemCount}
                  total={totalPackagingItemCount}
                />
              </ItemDescription>
            </ItemContent>
          </Item>
        </div>

        <div className='space-y-3'>
          <h3 className='text-lg font-semibold'>{t('details.packagingCatalog.title')}</h3>
          {packagedItems.length > 0 && (
            <PackedItemsFunnelProgressBar
              stats={packedItemsFunnelStats}
              labels={{
                unaddressed: {
                  label: t('stats.packedItemsUnaddressed'),
                  tooltip: t('stats.packedItemsUnaddressedTooltip'),
                },
                inProgress: {
                  label: t('stats.packedItemsInProgress'),
                  tooltip: t('stats.packedItemsInProgressTooltip'),
                },
                completed: {
                  label: t('stats.packedItemsCompleted'),
                  tooltip: t('stats.packedItemsCompletedTooltip'),
                },
              }}
            />
          )}
          {packagingSupplierId != null && (
            <SupplierDetailPackagingCatalog
              packagingSupplierId={packagingSupplierId}
              packagedItems={packagedItems}
              packagingItems={items}
              allPackagingItems={allPackagingItems}
              canWriteOrgData={canWriteOrgData}
              hasSupplierAddress={hasSupplierAddress}
              onAddItem={openAddItemDialog}
              onDeleteItem={(itemId) => {
                void deleteItemMutation.mutateAsync(itemId)
              }}
              isDeleting={deleteItemMutation.isPending}
            />
          )}
        </div>

        <div className='space-y-3'>
          <div className='space-y-1'>
            <h3 className='text-lg font-semibold'>
              {t('details.documentLibrary.title')}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {t('details.documentLibrary.description')}
            </p>
          </div>
          {packagingSupplierId != null && (
            <SupplierDocumentLibraryTable
              packagingSupplierId={packagingSupplierId}
            />
          )}
        </div>
      </Main>

      {canWriteOrgData && (
        <>
      <Dialog
        open={editingField != null}
        onOpenChange={(open) => {
          if (!open) {
            closeFieldEditor()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{fieldEditorTitle}</DialogTitle>
          </DialogHeader>
          <div className='space-y-1'>
            <Label htmlFor='supplier-field-edit'>{fieldEditorLabel}</Label>
            {editingField === 'address' ? (
              <Textarea
                id='supplier-field-edit'
                value={editValue}
                onChange={(event) => setEditValue(event.target.value)}
                autoFocus
              />
            ) : (
              <Input
                id='supplier-field-edit'
                type={editingField === 'contactEmail' ? 'email' : 'text'}
                value={editValue}
                onChange={(event) => setEditValue(event.target.value)}
                autoFocus
              />
            )}
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={closeFieldEditor}
              disabled={updateSupplierMutation.isPending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type='button'
              onClick={() => {
                void handleSaveField()
              }}
              disabled={
                updateSupplierMutation.isPending ||
                (editingField === 'name' &&
                  (editValue.trim().length === 0 ||
                    editValue.trim() === supplier.name)) ||
                (editingField === 'address' &&
                  (editValue.trim() || null) ===
                    (supplier.address?.trim() || null)) ||
                (editingField === 'contactName' &&
                  (editValue.trim() || null) ===
                    (supplier.contactName?.trim() || null)) ||
                (editingField === 'contactEmail' &&
                  ((editValue.trim() || null) ===
                    (supplier.contactEmail?.trim() || null) ||
                    !isValidOptionalEmail(editValue.trim())))
              }
            >
              {updateSupplierMutation.isPending
                ? t('details.saving')
                : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('invite.title')}</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>{t('invite.help')}</p>
          <div className='flex gap-2'>
            <Input readOnly value={supplier.magicLinkUrl} />
          </div>
          <div className='flex w-full flex-row items-center justify-between gap-2'>
            <Button
              type='button'
              variant='destructive'
              onClick={() => {
                void handleRotate()
              }}
              disabled={rotateMutation.isPending}
            >
              {t('invite.rotate')}
            </Button>
            <Button
              type='button'
              onClick={() => {
                void copyInviteUrl()
              }}
            >
              <Copy className='size-4' />
              {t('invite.copy')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (deleteSupplierMutation.isPending) {
            return
          }
          setIsDeleteConfirmOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('details.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('details.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSupplierMutation.isPending}>
              {t('details.deleteConfirmCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                buttonVariants({ variant: 'destructive' }),
                'gap-2'
              )}
              disabled={deleteSupplierMutation.isPending}
              onClick={(event) => {
                event.preventDefault()
                void handleDeleteSupplier()
              }}
            >
              {deleteSupplierMutation.isPending && (
                <Loader2 className='size-4 animate-spin' />
              )}
              {deleteSupplierMutation.isPending && t('details.deleting')}
              {!deleteSupplierMutation.isPending &&
                t('details.deleteConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={isAddItemOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeAddItemDialog()
          }
        }}
      >
        <DialogContent className='sm:max-w-3xl'>
          <DialogHeader>
            <DialogTitle>{addItemDialogTitle}</DialogTitle>
          </DialogHeader>
          {addItemTarget?.type === 'forPart' && (
            <div className='space-y-3'>
              <div className='rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm'>
                <p className='font-medium'>
                  {t('details.packagingCatalog.forPartLockedTitle')}
                </p>
                <p className='mt-1 text-muted-foreground'>
                  {t('details.packagingCatalog.forPartLockedDescription')}
                </p>
              </div>
              <PackagedItemMultiSelect
                options={packagedItems.map((part) => ({
                  id: part.id,
                  name: part.name,
                  sku: part.sku,
                }))}
                selectedIds={itemPackagedItemIds}
                onChange={setItemPackagedItemIds}
                disabled={createItemMutation.isPending}
                emptyMessage={t('details.packagingCatalog.forPartMultiSelectEmpty')}
              />
              {itemPackagedItemIds.length === 0 && (
                <p className='text-sm text-destructive'>
                  {t('details.packagingCatalog.forPartRequired')}
                </p>
              )}
            </div>
          )}
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='space-y-3'>
              <div className='space-y-1'>
                <Label htmlFor='item-name'>{t('items.form.name')}</Label>
                <Input
                  id='item-name'
                  value={itemName}
                  onChange={(event) => setItemName(event.target.value)}
                />
              </div>
              <div className='space-y-1'>
                <Label htmlFor='item-description'>
                  {t('items.form.description')}
                </Label>
                <Textarea
                  id='item-description'
                  value={itemDescription}
                  onChange={(event) => setItemDescription(event.target.value)}
                />
              </div>
            </div>
            <div className='space-y-1'>
              <Label htmlFor='item-intended-use'>
                {t('items.form.intendedUse')}
              </Label>
              <Textarea
                id='item-intended-use'
                value={itemIntendedUse}
                onChange={(event) => setItemIntendedUse(event.target.value)}
              />
            </div>
            <div className='grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2'>
              <div className='space-y-1'>
                <Label htmlFor='item-sku'>{t('items.form.sku')}</Label>
                <Input
                  id='item-sku'
                  value={itemSku}
                  onChange={(event) => setItemSku(event.target.value)}
                />
              </div>
              {addItemTarget?.type !== 'packedItem' &&
                addItemTarget?.type !== 'forPart' && (
                <div className='space-y-1 opacity-60'>
                  <Label htmlFor='item-packed-item-sku'>
                    {t('items.form.packedItemSku')}
                  </Label>
                  <Input
                    id='item-packed-item-sku'
                    value={itemPackedItemSku}
                    onChange={(event) =>
                      setItemPackedItemSku(event.target.value)
                    }
                  />
                </div>
              )}
            </div>
            <div className='sm:col-span-2'>
              <PackagingVolumeCm3Field
                id='add-item-volume-cm3'
                value={itemVolumeCm3Input}
                onChange={setItemVolumeCm3Input}
                disabled={createItemMutation.isPending}
                labels={{
                  label: t('items.form.volumeCm3'),
                  importantNoteTitle: t('items.form.volumeCm3ImportantNoteTitle'),
                  importantNoteBody: t('items.form.volumeCm3ImportantNoteBody'),
                  invalidMessage: t('items.form.volumeCm3Invalid'),
                }}
              />
            </div>
            <div className='sm:col-span-2'>
              <PackagingManufacturerSection
                supplierIsManufacturer={itemSupplierIsManufacturer}
                onSupplierIsManufacturerChange={setItemSupplierIsManufacturer}
                manufacturerName={itemManufacturerName}
                onManufacturerNameChange={setItemManufacturerName}
                manufacturerAddress={itemManufacturerAddress}
                onManufacturerAddressChange={setItemManufacturerAddress}
                supplierName={supplier.name}
                supplierAddress={supplier.address ?? ''}
                labels={manufacturerLabels}
                disabled={createItemMutation.isPending || !hasSupplierAddress}
              />
            </div>
            <div className='grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2'>
              <PackagingPpwrComplianceFields
                heavyMetalsStatus={itemHeavyMetalsStatus}
                substancesOfConcernStatus={itemSubstancesOfConcernStatus}
                onHeavyMetalsChange={setItemHeavyMetalsStatus}
                onSubstancesChange={setItemSubstancesOfConcernStatus}
                showSubstancesOfConcern={false}
                labels={ppwrComplianceLabels}
                disabled={createItemMutation.isPending}
              />
              <PackagingPpwrComplianceFields
                heavyMetalsStatus={itemHeavyMetalsStatus}
                substancesOfConcernStatus={itemSubstancesOfConcernStatus}
                onHeavyMetalsChange={setItemHeavyMetalsStatus}
                onSubstancesChange={setItemSubstancesOfConcernStatus}
                showHeavyMetals={false}
                labels={ppwrComplianceLabels}
                disabled={createItemMutation.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                void handleCreateItem()
              }}
              disabled={createItemMutation.isPending || !canCreateItem}
            >
              {t('common:save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </>
  )
}
